import { EstimateNotice } from "../ui/estimate-notice";
import { useFormikContext } from "formik";
import type { UnknownFormValues } from "../types/shipment-calculator.type";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  getEstimatedWeightRange,
  type ProductWeightEstimate,
} from "../helpers/product-weight-estimates";
import { formatNumber } from "@/helpers/pricing/processing-fee-helpers";
import type { ShipProcessingFee } from "@/schemas/ship";
import { ProductWeightSearch } from "../ui/product-weight-search";

function singularizeProductName(productName: string) {
  return productName.replace(/s(\s*(?:\([^)]*\))?)$/i, "$1");
}

export function CalculateForUnknownAir({
  inputClassName,
  processingFees,
}: {
  inputClassName: string;
  processingFees: ShipProcessingFee[];
}) {
  const { handleChange, setFieldValue, values } = useFormikContext<UnknownFormValues>();
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductWeightEstimate | null>(null);
  const debouncedProductType = useDebounce(values.productType, 1000);
  const debouncedQuantity = useDebounce(values.quantity, 1000);
  const quantity = Number(debouncedQuantity);
  const canEstimate = selectedProduct && Number.isFinite(quantity) && quantity > 0;

  const estimatedWeightRange = useMemo(
    () =>
      selectedProduct
        ? getEstimatedWeightRange(debouncedProductType, debouncedQuantity, selectedProduct)
        : null,
    [debouncedProductType, debouncedQuantity, selectedProduct],
  );
  const matchedProduct = estimatedWeightRange?.product ?? null;
  const estimatedMinWeight = estimatedWeightRange?.min ?? null;
  const estimatedMaxWeight = estimatedWeightRange?.max ?? null;
  const matchedProductName =
    matchedProduct && quantity === 1
      ? singularizeProductName(matchedProduct.name)
      : matchedProduct?.name;

  useEffect(() => {
    void setFieldValue(
      "estimatedMinWeight",
      estimatedMinWeight === null ? "" : String(estimatedMinWeight),
      false,
    );
    void setFieldValue(
      "estimatedMaxWeight",
      estimatedMaxWeight === null ? "" : String(estimatedMaxWeight),
      false,
    );
  }, [estimatedMaxWeight, estimatedMinWeight, setFieldValue]);

  const handleProductSearchChange = (value: string) => {
    void setFieldValue("productType", value);
  };

  const handleProductSelect = (product: ProductWeightEstimate) => {
    setSelectedProduct(product);
    void setFieldValue("productType", product.name);
  };

  useEffect(() => {
    const minRange = processingFees?.[0]?.start_range;
    const maxRange = processingFees?.[processingFees.length - 1]?.end_range;

    // No fee data loaded yet (or it failed to load) — nothing to validate against.
    if (minRange === undefined || maxRange === undefined) {
      setError(null);
      return;
    }

    if (
      (estimatedMinWeight && estimatedMinWeight < minRange) ||
      (estimatedMaxWeight && estimatedMaxWeight > maxRange)
    ) {
      setError(
        `Weight must be at least ${minRange} kg and at most ${maxRange} kg. Adjust quantity accordingly or refer to the "Processing Fees" guide under "Pricing" for more details.`,
      );
    } else {
      setError(null);
    }
  }, [estimatedMinWeight, estimatedMaxWeight, processingFees]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-xl font-medium text-[#323232]">Estimate by Product</h3>
        <p className="mt-5 text-base text-[#323232]">
          Select a product type to help us estimate the weight for you
        </p>
      </div>

      <ProductWeightSearch
        value={values.productType}
        inputClassName={inputClassName}
        onChange={handleProductSearchChange}
        onSelect={handleProductSelect}
        onClearSelection={() => setSelectedProduct(null)}
      />

      <label className="block">
        <span className="mb-4 block text-base font-normal text-[#323232]">Enter Quantity</span>
        <input
          name="quantity"
          type="number"
          min="1"
          value={values.quantity}
          onChange={handleChange}
          placeholder="Enter quantity"
          className={inputClassName}
        />
      </label>

      <div className="rounded-lg border border-[#b8c5d4] bg-[#f3f7fc] px-5 py-5">
        <h4 className="text-base font-medium text-[#323232]">Estimated weight range</h4>
        {matchedProduct && estimatedMinWeight !== null && estimatedMaxWeight !== null ? (
          <>
            <p className="mt-2 text-sm text-[#7a7a7a]">
              Based on {quantity} {quantity === 1 ? "of" : "pieces of"} {matchedProductName}
            </p>
            <div className="mt-6 grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-4 text-base">
              <span className="text-[#8b8b8b]">Estimated Min Weight</span>
              <span className="text-black">→</span>
              <span className="font-medium text-black">{formatNumber(estimatedMinWeight)}kg</span>
              <span className="text-[#8b8b8b]">Estimated Max Weight</span>
              <span className="text-black">→</span>
              <span className="font-medium text-black">{formatNumber(estimatedMaxWeight)}kg</span>
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-[#7a7a7a]">
            {canEstimate
              ? "Enter a valid quantity to estimate the weight range."
              : "Select a product and enter a quantity to estimate the weight range."}
          </p>
        )}
      </div>
      {error && <p className="mt-2 p-2 text-sm text-red-500">{error}</p>}
      <EstimateNotice />
    </div>
  );
}
